import { useNavigate } from "@tanstack/react-router";

import React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import HeadingLayout from "src/components/base/heading-layout";
import Form from "src/components/base/form";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import { ComputerDesktopIcon, MoonIcon, SunIcon } from "@heroicons/react/20/solid";
import { Button, PrimaryButton } from "src/components/base/button";
import LanguageSelect, { Lang } from "src/components/base/language-select";
import { Divider } from "src/components/base/divider";
import { ApiPublic } from "src/api/api_public";

/**
 * The properties for {@link Profile}
 */
export type ProfileProps = {};

/**
 * The settings for this site
 */
export default function Profile(props: ProfileProps) {
    const [t, i18n] = useTranslation("profile");
    const [tg] = useTranslation();

    const theme = localStorage.getItem("theme");
    const preferredLang = localStorage.getItem("preferredLang");

    const navigate = useNavigate();

    const form = useForm({
        defaultValues: {
            theme: theme ? theme : "system",
            preferredLanguage: preferredLang ? (preferredLang as Lang) : "EN",
        },
        onSubmit: async ({ value }) => {
            localStorage.setItem("preferredLang", value.preferredLanguage);
            await i18n.changeLanguage(value.preferredLanguage.toLowerCase());

            if (value.theme === "system") {
                localStorage.removeItem("theme");
            } else if (value.theme === "light") {
                localStorage.setItem("theme", "light");
            } else {
                localStorage.setItem("theme", "dark");
            }

            if (
                localStorage.theme === "dark" ||
                (!("theme" in localStorage) && window.matchMedia("(prefers-color-scheme: dark)").matches)
            ) {
                document.documentElement.classList.add("dark");
            } else {
                document.documentElement.classList.remove("dark");
            }
        },
    });

    /**
     * Logout of account and redirect to login page on success
     */
    const logout = async () => {
        await ApiPublic.auth.logout();
        await navigate({ to: "/" });
    };

    return (
        <HeadingLayout heading={t("heading.profile")}>
            <Form onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <div className="grid w-full grid-cols-1 items-center gap-x-4 gap-y-6 sm:grid-cols-2">
                            <form.Field name={"preferredLanguage"}>
                                {(fieldApi) => (
                                    <Field className="grid grid-cols-subgrid gap-3 sm:col-span-2">
                                        <Label>{t("label.preferred-lang")}</Label>
                                        <LanguageSelect lang={fieldApi.state.value} setLang={fieldApi.handleChange} />
                                    </Field>
                                )}
                            </form.Field>
                        </div>
                        <div className="grid w-full grid-cols-1 items-center gap-x-4 gap-y-6 sm:grid-cols-2">
                            <form.Field name={"theme"}>
                                {(fieldApi) => (
                                    <Field className="grid grid-cols-subgrid gap-3 sm:col-span-2">
                                        <Label>{t("label.theme")}</Label>
                                        <Listbox value={fieldApi.state.value} onChange={fieldApi.handleChange}>
                                            <ListboxOption value={"system"}>
                                                <ComputerDesktopIcon />
                                                <ListboxLabel>{t("label.theme-system")}</ListboxLabel>
                                            </ListboxOption>
                                            <ListboxOption value={"light"}>
                                                <SunIcon />
                                                <ListboxLabel>{t("label.theme-light")}</ListboxLabel>
                                            </ListboxOption>
                                            <ListboxOption value={"dark"}>
                                                <MoonIcon />
                                                <ListboxLabel>{t("label.theme-dark")}</ListboxLabel>
                                            </ListboxOption>
                                        </Listbox>
                                    </Field>
                                )}
                            </form.Field>
                        </div>

                        <Divider />
                        <div className={"flex justify-between"}>
                            <div className={"flex justify-end"}>
                                <Button plain={true} onClick={logout}>
                                    {tg("button.logout")}
                                </Button>
                            </div>
                            <div className={"flex justify-end"}>
                                <PrimaryButton type={"submit"}>{tg("button.save")}</PrimaryButton>
                            </div>
                        </div>
                    </FieldGroup>
                </Fieldset>
            </Form>
        </HeadingLayout>
    );
}
